import { useQuery } from "@tanstack/react-query";
import {
  createFailedConsultation,
  createPendingConsultation,
  ScrapersConsultation,
} from "@/domain/scrapers/consultation";
import { fetchScrapersConsultation } from "@/domain/scrapers/fetchScrapersConsultation";

interface UseScrapersConsultationOptions {
  enabled?: boolean;
  queryFn?: (plate: string) => Promise<ScrapersConsultation>;
  reuseCache?: boolean;
}

export function useScrapersConsultation(
  plate: string,
  options: UseScrapersConsultationOptions = {},
) {
  const enabled = Boolean(plate) && (options.enabled ?? true);
  const queryFn = options.queryFn ?? fetchScrapersConsultation;

  const query = useQuery<ScrapersConsultation>({
    queryKey: ["scrapers-consultation", plate],
    queryFn: () => queryFn(plate),
    enabled,
    placeholderData: createPendingConsultation(),
    staleTime: options.reuseCache ? Infinity : 0,
    retry: false,
    refetchOnMount: options.reuseCache ? false : "always",
    refetchInterval: (currentQuery) => {
      if (options.reuseCache) return false;
      if (currentQuery.state.status === "error") return false;
      return currentQuery.state.data?.isComplete ? false : 2000;
    },
    refetchOnWindowFocus: false,
  });

  return {
    consultation: !enabled
      ? createPendingConsultation()
      : query.isError
        ? createFailedConsultation()
        : (query.data ?? createPendingConsultation()),
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
